import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment.dev';
import { AuthStateService } from './auth-state.service';
import { TokenRefreshRequest, TokenRefreshResponse, TokenValidationResult } from '../model/auth.models';

@Injectable({ providedIn: 'root' })
export class TokenService {
  private readonly MIN_REFRESH_INTERVAL_MS = 5_000;
  private readonly JWT_WARNING_TIME_MS =
    (environment.JWT_EXPIRATION_WARNING_TIME_IN_MINUTES || 5) * 60 * 1000;

  // Per-company deduplication
  private refreshInProgress   = new Map<string, Promise<TokenRefreshResponse>>();
  private lastRefreshTimestamp = new Map<string, number>();
  private lastRefreshResponse  = new Map<string, TokenRefreshResponse>();

  constructor(
    private http: HttpClient,
    private authStateService: AuthStateService
  ) {}

  // ─── Token getters ────────────────────────────────────────────────────────

  /** Read the current token for a company from in-memory auth state only. */
  getToken(companyName: string): string | null {
    return this.authStateService.getCompanyTokenData(companyName)?.token || null;
  }

  getCurrentToken(): string | null {
    const company = this.authStateService.currentCompany;
    return company ? this.getToken(company) : null;
  }

  // ─── Token validation ─────────────────────────────────────────────────────

  validateToken(companyName: string): TokenValidationResult {
    const tokenData = this.authStateService.getCompanyTokenData(companyName);

    if (!tokenData?.token || !tokenData.expiresAt) {
      return { isValid: false, isExpired: true, expiresIn: 0, shouldRefresh: false };
    }

    const expiresIn = tokenData.expiresAt.getTime() - Date.now();
    return {
      isValid:       expiresIn > 0,
      isExpired:     expiresIn <= 0,
      expiresIn:     Math.max(0, expiresIn),
      shouldRefresh: expiresIn > 0 && expiresIn <= this.JWT_WARNING_TIME_MS
    };
  }

  // ─── Token refresh ────────────────────────────────────────────────────────

  async refreshToken(companyName: string): Promise<TokenRefreshResponse> {
    // Rate-limit: return cached result if refreshed within 5 seconds
    const now         = Date.now();
    const lastRefresh = this.lastRefreshTimestamp.get(companyName);
    if (lastRefresh && now - lastRefresh < this.MIN_REFRESH_INTERVAL_MS) {
      const cached = this.lastRefreshResponse.get(companyName);
      if (cached) return cached;
    }

    // Deduplication: reuse in-flight promise for same company
    const existing = this.refreshInProgress.get(companyName);
    if (existing) return existing;

    const refreshPromise = this.performTokenRefresh(companyName)
      .then(result => {
        this.lastRefreshTimestamp.set(companyName, Date.now());
        this.lastRefreshResponse.set(companyName, result);
        this.persistNewToken(companyName, result);
        return result;
      })
      .finally(() => this.refreshInProgress.delete(companyName));

    this.refreshInProgress.set(companyName, refreshPromise);
    return refreshPromise;
  }

  private async performTokenRefresh(companyName: string): Promise<TokenRefreshResponse> {
    const tokenData    = this.authStateService.getCompanyTokenData(companyName);
    const authUser     = this.authStateService.user;
    const userDetailsRaw = localStorage.getItem(`tibss.${companyName}-userDetails`);
    const localUser    = userDetailsRaw ? JSON.parse(userDetailsRaw) : null;

    const userID   = tokenData?.userId   || authUser?.userID   || localUser?.userID;
    const userName = authUser?.userName  || localUser?.userName || '';

    if (!userID || !userName) {
      throw new Error(`Cannot refresh token: missing user context for "${companyName}"`);
    }

    const body: TokenRefreshRequest = { userID, companyId: companyName, userName };

    const response: any = await firstValueFrom(
      this.http.post(
        `${environment.CAS_BASE_RUL}api/CAS/tokenRefresh`,
        body,
        { headers: { 'X-Client-Code': 'CRM' }, withCredentials: true }
      )
    );

    // V1 response shape: { data: { Token, data: [{ refreshToken }] } }
    const data = response?.data || response;
    const newToken       = data?.Token       || data?.data?.Token;
    const newRefreshToken = data?.data?.[0]?.refreshToken || data?.refreshToken;

    if (!newToken) {
      throw new Error(data?.message || `Token refresh failed for "${companyName}"`);
    }

    return {
      Status:       true,
      Token:        newToken,
      refreshToken: newRefreshToken,
      access_token_expired_at:  data?.access_token_expired_at,
      refresh_token_expired_at: data?.refresh_token_expired_at
    };
  }

  private persistNewToken(companyName: string, result: TokenRefreshResponse): void {
    const expiresAt = result.access_token_expired_at
      ? new Date(result.access_token_expired_at)
      : new Date(Date.now() + (environment.JWT_EXPIRATION_TIME_IN_MINUTES || 60) * 60 * 1000);

    const existing = this.authStateService.getCompanyTokenData(companyName);
    this.authStateService.updateCompanyToken(companyName, {
      ...existing,
      token:     result.Token,
      expiresAt,
      userId: existing?.userId
    });

    // Keep primary auth state in sync if this is the active company
    if (this.authStateService.currentCompany === companyName) {
      this.authStateService.setAuthState({
        token: result.Token,
        isAuthenticated: true,
        sessionExpiry: expiresAt,
        lastActivity: new Date()
      });
    }
  }
}
